// src/contexts/HelpContext.js
// ✅ TRACK-C FIX (Mar 28, 2026): Sidebar "Knowledge Base" → opens HelpDrawer
//
// WHY THIS EXISTS:
//   The HelpDrawer open/close state needs to be accessible from two places:
//     1. The Sidebar nav item "Knowledge Base" (onClick)
//     2. The HelpButton (floating ? button in App.js) — removed in favour of sidebar
//     3. DashboardLayout renders HelpDrawer at the top of the component tree
//
//   Without a context, we'd need to prop-drill `setHelpOpen` from App.js
//   through AppRoutes → DashboardLayout → Sidebar → NavItem — not viable.
//
//   This context is a minimal one-purpose provider: just open/close state.
//   It's added to the provider tree in App.js alongside UIProvider and ToastProvider.
//
// USAGE:
//   // Open the drawer from any component:
//   const { openHelp } = useHelp();
//   openHelp();
//
//   // Render the drawer (in DashboardLayout or App.js):
//   const { helpOpen, closeHelp } = useHelp();
//   <HelpDrawer open={helpOpen} onClose={closeHelp} />
// ─────────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useState, useCallback } from "react";

const HelpContext = createContext({
  helpOpen: false,
  openHelp: () => {},
  closeHelp: () => {},
  toggleHelp: () => {},
});

export function HelpProvider({ children }) {
  const [helpOpen, setHelpOpen] = useState(false);

  const openHelp  = useCallback(() => setHelpOpen(true),  []);
  const closeHelp = useCallback(() => setHelpOpen(false), []);
  const toggleHelp = useCallback(() => setHelpOpen(prev => !prev), []);

  return (
    <HelpContext.Provider value={{ helpOpen, openHelp, closeHelp, toggleHelp }}>
      {children}
    </HelpContext.Provider>
  );
}

export function useHelp() {
  return useContext(HelpContext);
}

export default HelpProvider;
